import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { getMyOrders, login, register, logout, subscribeToAuth } from '../lib/api';
import { Link } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import { ShoppingBag, Calendar, Package, LogOut, User, Mail, Lock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Account() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToAuth((u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  const { data: orders, isLoading: ordersLoading, mutate } = useSWR(
    user ? ['myOrders', user.uid] : 'myOrders', 
    getMyOrders
  );

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (isRegistering) {
        await register(email, password);
      } else {
        await login(email, password);
      }
      mutate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    mutate([]);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#b8892d]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-neutral-50 pt-32 pb-16">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white border border-neutral-200 p-8 shadow-sm">
            <h1 className="text-2xl font-serif text-center mb-8">
              {isRegistering ? 'Create Account' : 'Login to Account'}
            </h1>
            
            <form onSubmit={handleAuth} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-600 text-sm border border-red-100">
                  {error}
                </div>
              )}
              
              <div className="space-y-1">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-neutral-400" />
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="name@example.com"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-neutral-400" />
                  <Input 
                    id="password" 
                    type="password" 
                    placeholder="••••••••"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#b8892d] hover:bg-[#a07828] text-white"
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {isRegistering ? 'Register' : 'Login'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-neutral-500">
              {isRegistering ? (
                <p>
                  Already have an account?{' '}
                  <button 
                    onClick={() => setIsRegistering(false)} 
                    className="text-[#b8892d] font-medium hover:underline"
                  >
                    Login here
                  </button>
                </p>
              ) : (
                <p>
                  Don't have an account?{' '}
                  <button 
                    onClick={() => setIsRegistering(true)} 
                    className="text-[#b8892d] font-medium hover:underline"
                  >
                    Register here
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-serif text-neutral-900 mb-1">My Account</h1>
            <p className="text-neutral-500 flex items-center gap-2">
              <User className="w-4 h-4" /> {user.email}
            </p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Logout
          </Button>
        </div>
        
        <div className="bg-white border border-neutral-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
            <ShoppingBag className="w-5 h-5 text-[#b8892d]" /> My Orders
          </h2>
          
          {ordersLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : orders?.length > 0 ? (
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="border border-neutral-200 rounded p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-[#b8892d]/30 transition-colors">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-neutral-500 mb-1">
                      <span className="font-medium text-neutral-900">#{order.order_id || order.id.slice(-6).toUpperCase()}</span>
                      <span>•</span>
                      <Calendar className="w-4 h-4" />
                      <span>{order.created_at ? format(new Date(order.created_at), 'PPP') : 'Unknown date'}</span>
                    </div>
                    <div className="text-sm font-medium">
                      Status: <span className="uppercase text-xs tracking-wider text-[#b8892d] ml-1">{order.status.replace('_', ' ')}</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:items-end gap-1 text-sm">
                    <div className="font-bold text-lg text-neutral-900">
                      ₹{Number(order.total).toLocaleString('en-IN')}
                    </div>
                    <div className="text-neutral-500">
                      {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'items'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-neutral-50 border border-dashed border-neutral-300 rounded">
              <Package className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
              <p className="text-neutral-600 mb-6 text-lg">You haven't placed any orders yet.</p>
              <Button asChild className="bg-[#b8892d] hover:bg-[#a07828] text-white">
                <Link to="/shop">
                  Start Shopping
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
